import { Component, computed, effect, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { map, switchMap } from 'rxjs';

@Component({
  selector: 'ez-iban-input',
  imports: [ReactiveFormsModule, NgxMaskDirective],
  templateUrl: './iban-input.component.html',
  styleUrl: './iban-input.component.scss',
})
export class IbanInputComponent {
  readonly mask = 'UU00 AAAA AAAA AAAZ ZZZZ ZZZZ ZZZZ ZZZZ ZZ';
  private readonly rootFormGroup = inject(FormGroupDirective);

  readonly isDisabled = input<boolean>(false);
  readonly name = input.required<string>();
  readonly controlName = input.required<string>();

  get form(): FormGroup {
    return this.rootFormGroup.control;
  }

  readonly control = computed<AbstractControl>(() => this.form.get(this.controlName())!);

  readonly errorMessage = toSignal(
    toObservable(this.control).pipe(switchMap((control) => control.events.pipe(map(() => this.currentErrorMessage())))),
    { initialValue: null },
  );

  constructor() {
    effect(() => {
      const control = this.control();
      if (this.isDisabled()) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }

  private currentErrorMessage(): string | null {
    const control = this.control();
    if (!control.invalid || !(control.dirty || control.touched)) {
      return null;
    }
    if (control.errors?.['required']) {
      return 'Dies ist ein Pflichtfeld.';
    }
    if (control.errors?.['iban']) {
      return 'Die eingegebene Iban ist fehlerhaft.';
    }
    return 'Fehlerhafte Eingabe.';
  }

  inputTransformFn = (value: unknown): string => {
    return typeof value === 'string' ? value.toUpperCase() : String(value);
  };

  outputTransformFn = (value: string | number | null | undefined): string => {
    return value ? String(value).toUpperCase() : '';
  };
}
